import React from 'react';
import classNames from 'classnames';
import { format, isToday } from 'date-fns';
import { es } from 'date-fns/locale';
import { capitalize } from '../../utils/textUtils';
import './ActivityTimeline.scss';

export type ActivityTimelineItem = {
  id: string;
  start: Date;
  /** Shown before the title, for example "09:00 – 10:00". Empty for an all-day event. */
  time?: string;
  title: string;
  /** A palette name (green, blue, pink...), the same color the calendar gives the event. */
  color: string;
  details?: string[];
  /** Shown at the right: a status ("Cancelada") or an amount ("30€"). */
  aside?: { label: string; tone?: 'danger' | 'muted' | 'strong' };
  /** Opens the event. Without it the item is not interactive. */
  onSelect?: () => void;
};

const dayKey = (date: Date) => format(date, 'yyyy-MM-dd');

const ActivityTimelineEntry = ({ item }: { item: ActivityTimelineItem }) => {
  const content = (
    <>
      <span className='activity-timeline-time'>{item.time}</span>
      <span className='activity-timeline-dot' style={{ backgroundColor: `var(--${item.color}-500)` }} aria-hidden />
      <span className='activity-timeline-body'>
        <span className='activity-timeline-title'>{item.title}</span>
        {item.details && item.details.length > 0 && <span className='activity-timeline-details'>{item.details.join(' · ')}</span>}
      </span>
      {item.aside && <span className={classNames('activity-timeline-aside', item.aside.tone)}>{item.aside.label}</span>}
    </>
  );
  if (!item.onSelect) return <div className='activity-timeline-item'>{content}</div>;
  return (
    <button type='button' className='activity-timeline-item' onClick={item.onSelect}>
      {content}
    </button>
  );
};

/** The events of a period as a list of days, oldest first, each day with its events by start time. */
export const ActivityTimeline = ({ items, emptyMessage }: { items: ActivityTimelineItem[]; emptyMessage: string }) => {
  if (items.length === 0) return <p className='activity-timeline-empty'>{emptyMessage}</p>;

  const sorted = [...items].sort((a, b) => a.start.getTime() - b.start.getTime());
  const days = new Map<string, ActivityTimelineItem[]>();
  for (const item of sorted) days.set(dayKey(item.start), [...(days.get(dayKey(item.start)) ?? []), item]);

  return (
    <ol className='activity-timeline'>
      {Array.from(days.values()).map((dayItems) => {
        const day = dayItems[0].start;
        return (
          <li key={dayKey(day)} className={classNames('activity-timeline-day', { today: isToday(day) })}>
            <h4>{capitalize(format(day, "EEEE, d 'de' MMMM", { locale: es }))}</h4>
            <ul>
              {dayItems.map((item) => (
                <li key={item.id}>
                  <ActivityTimelineEntry item={item} />
                </li>
              ))}
            </ul>
          </li>
        );
      })}
    </ol>
  );
};
